<template>
  <div class="time-range-filter">
    <div class="filter-container">
      <!-- 时间类型下拉选择 -->
      <div class="time-type-select">
        <a-select
          v-model="quickType"
          style="width: 120px"
          @change="handleQuickChange"
        >
          <a-select-option value="all">全部时间</a-select-option>
          <a-select-option value="today">今天</a-select-option>
          <a-select-option value="yesterday">昨天</a-select-option>
          <a-select-option value="week">近一周</a-select-option>
          <a-select-option value="custom">自定义时间</a-select-option>
        </a-select>
      </div>

      <!-- 时间范围选择器 -->
      <div class="time-range-picker">
        <a-range-picker
          v-model="timeRange"
          show-time
          format="YYYY-MM-DD HH:mm:ss"
          :placeholder="['开始时间', '结束时间']"
          :disabled="quickType !== 'custom'"
          @change="handleTimeRangeChange"
          @openChange="handleOpenChange"
          style="width: 387px"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { mapGetters } from 'vuex'

export default {
  name: 'TimeRangeFilter',
  props: {
    // 初始化的快捷类型
    defaultType: {
      type: String,
      default: 'today'
    }
  },
  data () {
    return {
      quickType: this.defaultType,
      timeRange: [],
      isUserEditing: false // 标记用户是否正在手动编辑
    }
  },
  computed: {
    ...mapGetters('timezone', ['currentTimezone', 'currentTimezoneInfo'])
  },
  mounted () {
    // 初始化默认时间范围
    this.initDefaultRange()
  },
  watch: {
    // 监听时区变化，重新发送时间数据
    currentTimezoneInfo: {
      handler () {
        // 如果已经有时间范围，重新发送数据
        if (this.timeRange && this.timeRange.length === 2) {
          this.handleQuickChange()
        }
      },
      deep: true
    }
  },
  methods: {
    // 初始化默认时间范围
    initDefaultRange () {
      this.handleQuickChange()
    },

    // 处理快捷时间类型变化
    handleQuickChange () {
      // 如果是用户手动编辑导致的变化，不处理
      if (this.isUserEditing) {
        this.isUserEditing = false
        return
      }

      let startTime, endTime

      // 根据当前时区创建时间
      const currentMoment = this.currentTimezone ? moment.tz(this.currentTimezone) : moment()

      switch (this.quickType) {
        case 'all':
          // 时间选择器不展示
          startTime = null
          endTime = null
          this.timeRange = []
          break
        case 'today':
          startTime = currentMoment.clone().startOf('day')
          endTime = currentMoment.clone().endOf('day').subtract(999, 'milliseconds')
          this.timeRange = [startTime, endTime]
          break
        case 'yesterday':
          startTime = currentMoment.clone().subtract(1, 'days').startOf('day')
          endTime = currentMoment.clone().subtract(1, 'days').endOf('day').subtract(999, 'milliseconds')
          this.timeRange = [startTime, endTime]
          break
        case 'week':
          // 算上今天
          startTime = currentMoment.clone().subtract(6, 'days').startOf('day')
          endTime = currentMoment.clone().endOf('day').subtract(999, 'milliseconds')
          this.timeRange = [startTime, endTime]
          break
        case 'custom':
          // 自定义时间重新设置时区信息
          if (this.timeRange && this.timeRange.length === 2) {
            const startTime = moment.tz(this.timeRange[0].format('YYYY-MM-DD HH:mm:ss'), this.currentTimezone)
            const endTime = moment.tz(this.timeRange[1].format('YYYY-MM-DD HH:mm:ss'), this.currentTimezone)
            this.timeRange = [startTime, endTime]
          }
          break
      }

      this.emitChange()
    },

    // 处理时间范围手动变化
    handleTimeRangeChange (dates) {
      // 如果不是自定义时间模式，且用户手动修改了时间，自动切换到自定义模式
      if (this.quickType !== 'custom' && dates && dates.length === 2) {
        this.isUserEditing = true
        this.quickType = 'custom'
      }

      this.timeRange = dates || []
      this.emitChange()
    },

    // 处理时间选择器打开/关闭
    handleOpenChange (open) {
      // 当时间选择器关闭时，如果有值且不是自定义模式，切换到自定义模式
      if (!open && this.timeRange && this.timeRange.length === 2 && this.quickType !== 'custom') {
        // 检查时间是否被用户修改了
        const isModified = this.checkIfTimeModified()
        if (isModified) {
          this.isUserEditing = true
          this.quickType = 'custom'
        }
      }
    },

    // 检查时间是否被用户修改
    checkIfTimeModified () {
      if (!this.timeRange || this.timeRange.length !== 2) return false

      const currentStart = this.timeRange[0]
      const currentEnd = this.timeRange[1]

      let expectedStart, expectedEnd

      // 根据当前时区创建时间
      const currentMoment = this.currentTimezone ? moment.tz(this.currentTimezone) : moment()

      switch (this.quickType) {
        case 'today':
          expectedStart = currentMoment.clone().startOf('day')
          expectedEnd = currentMoment.clone().endOf('day').subtract(999, 'milliseconds')
          break
        case 'yesterday':
          expectedStart = currentMoment.clone().subtract(1, 'days').startOf('day')
          expectedEnd = currentMoment.clone().subtract(1, 'days').endOf('day').subtract(999, 'milliseconds')
          break
        case 'week':
          expectedStart = currentMoment.clone().subtract(7, 'days').startOf('day')
          expectedEnd = currentMoment.clone().endOf('day').subtract(999, 'milliseconds')
          break
        default:
          return false
      }

      // 比较时间是否一致（允许秒级差异）
      const startDiff = Math.abs(currentStart.diff(expectedStart, 'seconds'))
      const endDiff = Math.abs(currentEnd.diff(expectedEnd, 'seconds'))

      return startDiff > 1 || endDiff > 1
    },

    // 发送时间变化事件
    emitChange () {
      const result = {
        quickType: this.quickType,
        startTime: null,
        endTime: null,
        startTimestamp: null,
        endTimestamp: null
      }

      if (this.timeRange && this.timeRange.length === 2) {
        // 根据全局时区转换为时间戳
        result.startTime = this.convertToTimezoneTimestamp(this.timeRange[0])
        result.endTime = this.convertToTimezoneTimestamp(this.timeRange[1])
      }

      this.$emit('change', result)
    },

    // 根据当前时区转换为时间戳
    convertToTimezoneTimestamp (momentObj) {
      // 如果当前时区已经是上海时区，直接返回时间戳
      if (this.currentTimezone === 'Asia/Shanghai') {
        return momentObj.valueOf()
      }

      // 先将时间解析为当前时区，再转换为上海时区
      const timeInCurrentTz = moment.tz(momentObj.format('YYYY-MM-DD HH:mm:ss'), this.currentTimezone)
      const timeInShanghai = timeInCurrentTz.clone().tz('Asia/Shanghai')
      return timeInShanghai.valueOf()
      // if (!this.currentTimezoneInfo) {
      //   return momentObj.valueOf()
      // }

      // // 默认时区是 UTC+0
      // const defaultOffset = 0
      // // 目标时区偏移量
      // const targetOffset = this.currentTimezoneInfo.offset

      // // 计算时区差异（小时）
      // const offsetDiff = targetOffset + defaultOffset

      // // 转换为毫秒并调整时间戳
      // const offsetDiffMs = offsetDiff * 60 * 60 * 1000
      // return momentObj.valueOf() + offsetDiffMs
    },

    // 重置为默认时间
    resetToDefault () {
      this.quickType = this.defaultType
      this.timeRange = []
      this.isUserEditing = false
      this.handleQuickChange()
    },

    // 手动设置时间范围
    setTimeRange (startTime, endTime) {
      this.quickType = 'custom'

      // 处理时间戳或时间字符串
      let startMoment, endMoment

      if (typeof startTime === 'number') {
        // 如果是数字时间戳，直接使用
        startMoment = moment(startTime)
      } else if (typeof startTime === 'string') {
        // 如果是字符串，先尝试转换为数字（处理字符串格式的时间戳）
        const startTimeNum = parseInt(startTime)
        if (!isNaN(startTimeNum)) {
          startMoment = moment(startTimeNum)
        } else {
          // 如果不是数字，尝试作为时间字符串解析
          startMoment = moment(startTime)
        }
      } else {
        // 其他情况，使用当前时间
        startMoment = moment()
      }

      if (typeof endTime === 'number') {
        // 如果是数字时间戳，直接使用
        endMoment = moment(endTime)
      } else if (typeof endTime === 'string') {
        // 如果是字符串，先尝试转换为数字（处理字符串格式的时间戳）
        const endTimeNum = parseInt(endTime)
        if (!isNaN(endTimeNum)) {
          endMoment = moment(endTimeNum)
        } else {
          // 如果不是数字，尝试作为时间字符串解析
          endMoment = moment(endTime)
        }
      } else {
        // 其他情况，使用当前时间
        endMoment = moment()
      }

      // 验证时间是否有效
      if (!startMoment.isValid()) {
        console.warn('Invalid startTime:', startTime)
        startMoment = moment()
      }
      if (!endMoment.isValid()) {
        console.warn('Invalid endTime:', endTime)
        endMoment = moment()
      }

      // 如果设置了时区，将时间转换为指定时区
      if (this.currentTimezone) {
        startMoment = startMoment.tz(this.currentTimezone)
        endMoment = endMoment.tz(this.currentTimezone)
      }

      this.timeRange = [startMoment, endMoment]
      this.emitChange()
    },

    // 获取当前时间范围
    getCurrentRange () {
      if (this.timeRange && this.timeRange.length === 2) {
        return {
          startTime: this.timeRange[0].format('YYYY-MM-DD HH:mm:ss'),
          endTime: this.timeRange[1].format('YYYY-MM-DD HH:mm:ss'),
          startTimestamp: this.convertToTimezoneTimestamp(this.timeRange[0]),
          endTimestamp: this.convertToTimezoneTimestamp(this.timeRange[1])
        }
      }
      return {
        startTime: null,
        endTime: null,
        startTimestamp: null,
        endTimestamp: null
      }
    }
  }
}
</script>

<style lang="less" scoped>
.time-range-filter {
  .filter-container {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    .time-type-select {
      flex-shrink: 0;
    }

    .time-range-picker {
      flex-shrink: 0;
    }
  }

  // 禁用状态的样式优化
  .time-range-picker {
    :global(.ant-picker-disabled) {
      background-color: #f5f5f5;
      border-color: #d9d9d9;
      cursor: not-allowed;

      :global(.ant-picker-input) {
        color: #999;
      }
    }
  }

  // 响应式设计
  @media (max-width: 768px) {
    .filter-container {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      .time-type-select {
        width: 100%;

        :global(.ant-select) {
          width: 100% !important;
        }
      }

      .time-range-picker {
        width: 100%;

        :global(.ant-picker) {
          width: 100% !important;
        }
      }
    }
  }
}
</style>
